import React, { useState, useEffect, useContext } from "react";
import { Table, Spin, Tooltip, Tag, Modal, Typography } from "antd";
import { LoadingOutlined, EyeOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { adminContext } from "../../Common/PageRoute";
import SuperAdminLogService from "../../Services/SuperAdminLogService";
import { PageHeader, PageLayout, SectionCard, TableToolbar } from "../../Common/Layout";

// Mirrors ApplicationModuleSubType enum (C# backend)
const SUB_MODULE_MAP: Record<number, string> = {
  0: "-",
  1: "General Settings",
  2: "Basic Information",
  3: "Bank Accounts",
  4: "TimeZone & Email",
  5: "Settings & Reviews",
  6: "Features",
};
// Mirrors ApplicationModules enum (C# backend)
export const MODULE_MAP: Record<number, string> = {
  0: "-",
  1: "Practice",
  2: "User",
  3: "Holiday",
  4: "CannedMessage",
  5: "Ticket",
  6: "Payroll",
  7: "BookKeeping",
  8: "Update",
  9: "CTConfiguration",
  10: "SubmissionHold",
  11:"Xcelify",
  12:"FyiImport",
};

const ACTION_MAP: Record<number, { label: string; color: string }> = {
  1: { label: "Created", color: "green" },
  2: { label: "Updated", color: "blue" },
  3: { label: "Deleted", color: "red" },
};

interface AuditUserInfo {
  userId?: string;
  userName?: string | null;
  date?: string;
}

interface SuperAdminLogItem {
  id: string;
  subModule: number;
  module: number;
  entityId: string;
  entityName: string;
  action: number;
  message: string;
  ipAddress: string;
  userAgent: string | null;
  recordStatus: number;
  createdBy?: AuditUserInfo | null;
}

interface SuperAdminLogsListState {
  items: SuperAdminLogItem[];
  totalItems: number;
}

// Default range: epoch -> today. Used as the "no filter applied" sentinel.
const defaultDateRange: [Dayjs, Dayjs] = [dayjs("1970-01-01"), dayjs()];

const getDateFromObjectId = (objectId: string): Date | null => {
  if (!objectId || objectId.length < 8) return null;
  const timestampHex = objectId.substring(0, 8);
  const timestampSeconds = parseInt(timestampHex, 16);
  if (Number.isNaN(timestampSeconds)) return null;
  return new Date(timestampSeconds * 1000);
};

const formatDateTime = (value: string | Date | null | undefined) => {
  let parsedDate: Date | null = null;

  if (value instanceof Date) {
    parsedDate = value;
  } else if (typeof value === "string") {
    const directDate = new Date(value);
    if (!Number.isNaN(directDate.getTime())) {
      parsedDate = directDate;
    } else {
      parsedDate = getDateFromObjectId(value);
    }
  }

  if (!parsedDate || Number.isNaN(parsedDate.getTime())) return "-";

  return parsedDate.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const isDefaultDateRange = (value?: [Dayjs, Dayjs]) => {
  if (!value?.[0] || !value?.[1]) return true;
  return (
    value[0].isSame(dayjs("1970-01-01"), "day") &&
    value[1].isSame(dayjs(), "day")
  );
};

const SuperAdminLogsList: React.FC = () => {
  const { openNotification } = useContext(adminContext);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [dataList, setDataList] = useState<SuperAdminLogsListState>({
    items: [],
    totalItems: 0,
  });

  const [messageModal, setMessageModal] = useState<{
    visible: boolean;
    record?: SuperAdminLogItem;
  }>({ visible: false });

  const [listParams, setListParams] = useState<{
    search: string;
    dateRange: [Dayjs, Dayjs];
    subModule?: number;
    module?: number;
    action?: number;
    sortDir: string;
    sortCol: string;
    currentPage: number;
    pageSize: number;
  }>({
    search: "",
    dateRange: defaultDateRange,
    subModule: undefined,
    module: undefined,
    action: undefined,
    sortDir: "descend",
    sortCol: "Id",
    currentPage: 1,
    pageSize: 15,
  });

  const fetchItems = async (start: number, length: number) => {
    setLoading(true);
    try {
      const fromDate = isDefaultDateRange(listParams.dateRange)
        ? undefined
        : listParams.dateRange[0].startOf("day").toISOString();
      const toDate = isDefaultDateRange(listParams.dateRange)
        ? undefined
        : listParams.dateRange[1].endOf("day").toISOString();

      const res = await SuperAdminLogService.getAdminLogList(
        start,
        length,
        listParams.search,
        listParams.sortCol,
        listParams.sortDir === "ascend" ? "asc" : "desc",
        fromDate,
        toDate,
        listParams.action,
        listParams.subModule,
        listParams.module
      );

      if (res?.status && res?.result) {
        setDataList({
          totalItems: res.result.totalRecords ?? 0,
          items: res.result.items ?? [],
        });
      } else {
        openNotification("error", res?.message || "Failed to fetch super admin logs");
      }
    } catch (err) {
      openNotification("error", "Error fetching super admin logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(
      (listParams.currentPage - 1) * listParams.pageSize,
      listParams.pageSize
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParams, refresh]);

  const renderMessage = (message: string) => {
    const html = (message || "").replace(/\r\n/g, "<br/>");
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const columns: ColumnsType<SuperAdminLogItem> = [
    {
      title: "S.No",
      key: "sno",
      width: "6%",
      render: (_, __, index) =>
        listParams.currentPage > 1
          ? listParams.pageSize * (listParams.currentPage - 1) + index + 1
          : index + 1,
    },
     {
      title: "Logs",
      key: "message",
      width: "22%",
      render: (_, record) => (
        <Typography.Paragraph
          style={{
            marginBottom: 0,
            marginTop: 0,
            height: 25,
            lineHeight: "25px",
          }}
          ellipsis={{
            rows: 1,
            tooltip: {
              placement: "topLeft",
              overlayStyle: { maxWidth: 700 },
            },
          }}
        >
          {renderMessage(record.message)}
        </Typography.Paragraph>
      ),
    },
    {
      title: "Module",
      key: "module",
      width: "14%",
      render: (_, record) => (
        <span>
          {MODULE_MAP[record.module] ?? String(record.module)}
        </span>
      ),
    },
    {
      title: "Sub-module",
      dataIndex: "subModule",
      key: "SubModule",
      width: "10%",
      render: (_, record) => (
        <span>
          {SUB_MODULE_MAP[record.subModule] ?? record.subModule}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "Action",
      width: "10%",
      render: (_, record) => {
        const action = ACTION_MAP[record.action];
        return (
          <Tag color={action?.color ?? "default"}>
            {action?.label ?? record.action}
          </Tag>
        );
      },
    },
    {
      title: "Date",
      key: "Date",
      width: "12%",
      render: (_, record) => formatDateTime(record.createdBy?.date),
    },
    {
      title: "User",
      key: "userName",
      width: "12%",
      render: (_, record) => <span>{record.createdBy?.userName ?? "-"}</span>,
    },
    {
      title: "",
      key: "view",
      width: "6%",
      render: (_, record) => (
        <Tooltip title="View full details">
          <EyeOutlined
            style={{ cursor: "pointer" }}
            onClick={() => setMessageModal({ visible: true, record })}
          />
        </Tooltip>
      ),
    },
  ];

  const getFilterOptions = (map: Record<number, string>) =>
    Object.entries(map)
      .filter(([value]) => Number(value) !== 0)
      .map(([value, label]) => ({ value, label }));

  const filterCriteria = [
    {
      key: "dateRange",
      label: "Date Range",
      type: "dateRange" as const,
    },
    {
      key: "subModule",
      label: "Sub-module",
      type: "select" as const,
      options: [
        { value: "", label: "All" },
        ...getFilterOptions(SUB_MODULE_MAP),
      ],
      placeholder: "Select Sub-module",
    },
    {
      key: "module",
      label: "Module",
      type: "select" as const,
      options: [
        { value: "", label: "All" },
        ...getFilterOptions(MODULE_MAP),
      ],
      placeholder: "Select Module",
    },
    {
      key: "action",
      label: "Action",
      type: "select" as const,
      options: [
        { value: "", label: "All" },
        ...getFilterOptions(Object.fromEntries(
          Object.entries(ACTION_MAP).map(([value, { label }]) => [value, label])
        )),
      ],
      placeholder: "Select Action",
    },
  ];

  const activeFilters = {
    ...(isDefaultDateRange(listParams.dateRange)
      ? {}
      : {
          dateRange:
            listParams.dateRange?.[0] && listParams.dateRange?.[1]
              ? [listParams.dateRange[0], listParams.dateRange[1]]
              : undefined,
        }),
    subModule: listParams.subModule !== undefined ? String(listParams.subModule) : "",
    module: listParams.module !== undefined ? String(listParams.module) : "",
    action: listParams.action !== undefined ? String(listParams.action) : "",
  };

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    const dates = Array.isArray(newFilters.dateRange) ? newFilters.dateRange : [null, null];
    const nextDateRange: [Dayjs, Dayjs] = dates[0] && dates[1]
      ? [dates[0] as Dayjs, dates[1] as Dayjs]
      : defaultDateRange;

    const normalizeFilterValue = (value: any) => {
      if (value === undefined || value === null || value === "") return undefined;
      const numericValue = Number(value);
      return numericValue === 0 ? undefined : numericValue;
    };

    setListParams((prev) => ({
      ...prev,
      dateRange: nextDateRange,
      subModule: normalizeFilterValue(newFilters.subModule),
      module: normalizeFilterValue(newFilters.module),
      action: normalizeFilterValue(newFilters.action),
      currentPage: 1,
    }));
  };

  return (
    <PageLayout>
      <PageHeader title="Super Admin Logs" subtitle="Audit trail for administrative changes and actions" />

      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            onSearch={(val: string) =>
              setListParams({ ...listParams, search: val, currentPage: 1 })
            }
            searchPlaceholder="Search logs..."
            filterCriteria={filterCriteria}
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            onRefresh={() => setRefresh(!refresh)}
          />
        </div>

        <Table
          dataSource={dataList.items}
          columns={columns}
          rowKey="id"
          size="small"
          scroll={{ x: 1300, y: window.innerHeight - 256 }}
          loading={{
            spinning: loading,
            indicator: <Spin indicator={<LoadingOutlined />} />,
          }}
          className="border-top-table"
          pagination={{
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            current: listParams.currentPage,
            pageSize: listParams.pageSize,
            showSizeChanger: true,
            total: dataList.totalItems,
            pageSizeOptions: ["15", "25", "50", "100"],
          }}
          onChange={(paging, _filter, sort: any) => {
            if (paging?.current !== undefined && paging?.pageSize !== undefined) {
              setListParams({
                ...listParams,
                sortDir: sort.order !== undefined ? sort.order : "descend",
                sortCol: sort.order !== undefined ? sort.columnKey : "Id",
                currentPage: paging.current,
                pageSize: paging.pageSize,
              });
            }
          }}
        />
      </SectionCard>

      <Modal
        open={messageModal.visible}
        title={messageModal.record?.entityName}
        footer={null}
        onCancel={() => setMessageModal({ visible: false })}
      >
        {messageModal.record && (
          <>
            <p>
              <strong>Module:</strong>{" "}
              {MODULE_MAP[messageModal.record.module] ??
                messageModal.record.module}
            </p>
            <p>
              <strong>Action:</strong>{" "}
              {ACTION_MAP[messageModal.record.action]?.label ?? messageModal.record.action}
            </p>
            <p>
              <strong>SubModule:</strong>{" "}
              {SUB_MODULE_MAP[messageModal.record.subModule] ??
                messageModal.record.subModule}
            </p>
            <p>
              <strong>Created By:</strong>{" "}
              {messageModal.record.createdBy?.userName ?? "-"}
              {messageModal.record.createdBy?.date
                ? ` on ${formatDateTime(messageModal.record.createdBy.date)}`
                : ""}
            </p>
            <p>
              <strong>Message:</strong>{" "}
              {renderMessage(messageModal.record.message)}
            </p>
            
          </>
        )}
      </Modal>
    </PageLayout>
  );
};

export default SuperAdminLogsList;